import { MutableRefObject, useMemo, useRef, useState } from "react";

import { RichTextDocument, RichTextEditorRef, RichTextValue } from "./types";

export interface UseRichTextOptions {
  initial: string | null | undefined;
  loading?: boolean;
  triggerChange: () => void;
}

export interface UseRichTextResult {
  editorRef: MutableRefObject<RichTextEditorRef | null>;
  handleChange: () => void;
  getValue: () => Promise<RichTextDocument | null>;
  defaultValue: RichTextValue;
  isReadyForMount: boolean;
  isDirty: boolean;
}

export function useRichText({
  initial,
  loading,
  triggerChange,
}: UseRichTextOptions): UseRichTextResult {
  const editorRef = useRef<RichTextEditorRef | null>(null);
  const [isReadyForMount, setIsReadyForMount] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const handleChange = () => {
    setIsDirty(true);
    triggerChange();
  };
  const getValue = async () => {
    if (editorRef.current) {
      setIsDirty(false);

      return editorRef.current.save();
    } else {
      throw new Error("Editor instance is not available");
    }
  };

  const defaultValue = useMemo<RichTextValue>(() => {
    if (loading) {
      return;
    }

    setIsDirty(false);
    setIsReadyForMount(true);

    if (!initial) {
      return null;
    }

    try {
      return JSON.parse(initial) as RichTextDocument;
    } catch (e) {
      return initial;
    }
  }, [initial, loading]);

  return {
    isDirty,
    editorRef,
    handleChange,
    getValue,
    defaultValue,
    isReadyForMount,
  };
}

export default useRichText;
